import { createFileRoute } from '@tanstack/react-router';
import { z } from 'zod';
import ApplyPage from '@/components/ApplyPage/ApplyPage';
import { createApplyPageHead } from '@/lib/seo';
import { SessionNotAvailable } from '@/components/SessionNotAvailable';
import { loadPublicTemplate } from '@/lib/loadPublicTemplate';
import { getDevSample } from '@/components/ApplyPage/devSamples';

// Dev-only preview params (ignored in production builds):
//   ?theme=violet       overrides the company theme
//   ?sample=long-title  renders a fake template from devSamples (no API call)
const applySearchSchema = z.object({
  theme: z.string().optional(),
  sample: z.string().optional(),
});

export const Route = createFileRoute('/apply/$publicId/')({
  component: ApplyPageComponent,
  validateSearch: applySearchSchema,
  loaderDeps: ({ search }) => ({ sample: search.sample }),
  loader: async ({ params, deps }) => {
    const devSample = getDevSample(deps.sample);
    if (devSample) return { vacancy: devSample };
    return loadPublicTemplate(params.publicId);
  },
  head: ({ loaderData, params }) => {
    const vacancy = loaderData?.vacancy;
    const vacancyName = vacancy?.name || 'Job Application';
    const companyName = vacancy?.company_info?.name || 'Company';
    const companyLogo = vacancy?.company_info?.profile_image || undefined;

    return createApplyPageHead({
      vacancyName,
      companyName,
      companyLogo,
      publicId: params.publicId,
    });
  },
});

function ApplyPageComponent() {
  const { vacancy, unavailableReason } = Route.useLoaderData();
  const { theme, sample } = Route.useSearch();

  if (!vacancy) {
    return <SessionNotAvailable reason={unavailableReason} />;
  }

  return (
    <ApplyPage
      vacancy={vacancy}
      themeOverride={import.meta.env.DEV ? theme : undefined}
      isPreview={Boolean(getDevSample(sample))}
    />
  );
}
